import { useContext, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/router';
import { Layout } from 'antd-v3';
import { combineClassNames } from '@arkestro/arkestro-design-system';
import { CloseOutlined } from '@ant-design/icons';

import { UserContext } from '@providers/UserProvider/UserProvider';
import { LayoutContext } from '@providers/LayoutProvider/LayoutProvider';
import { ConversationsContext } from '@providers/ConversationsProvider/ConversationsProvider';
import { GeneralContext } from '@providers/GeneralProvider/GeneralProvider';
import { collapsedIntercomLeft, expandedIntercomLeft } from '@services/IntercomService';
import { useFilteredUnreadBadgeCount } from '@hooks/useFilteredUnreadBadgeCount';
import { useIsAdminChannelOptInEnabled } from '@hooks/useIsAdminChannelOptInEnabled';

import { ArkestroLogo } from '@components/LogoImage';
import { useRfxDrawer } from '@components/providers/RfxDrawerProvider/RfxDrawerProvider';
import style from './SideMenu.module.scss';
import Footer from './Footer';
import Backdrop from './Backdrop';
import { getSideMenuItems } from './utils/getSideMenuItems';
import { getHelpMenuItemData } from './utils/getHelpMenuItemData';
import { isActiveLink, LinkItem } from './LinkItem';

export const SIDER_MIN_WIDTH = 80; // set by AntD
export const SIDER_MAX_WIDTH = 240;

/**
 * LaunchDarkly Flags: enableValueOverviewDashboard, enableAdoptionReporting, useNewSupportSite, enableNewSettingsPage
 */
export const SideMenu = () => {
  const router = useRouter();
  const conversationsContext = useContext(ConversationsContext);
  const { filteredUnreadCount, unreadCount } = conversationsContext;
  const { isError: isBadgeFilterError } = useFilteredUnreadBadgeCount();
  const isAdminChannelOptInEnabled = useIsAdminChannelOptInEnabled();
  const { t } = useTranslation('common');
  const user = useContext(UserContext);
  const { isAutomated } = useContext(GeneralContext);
  const { toggleSideMenu, collapsed } = useContext(LayoutContext);

  const {
    enableCatalog = false,
    enableNewSettingsPage = false,
    enablePriceUpdate = false,
    enableValueOverviewDashboard = false,
    enableAdoptionReporting = false,
  } = useFlags();
  const { openRfxDrawer, currentPathName } = useRfxDrawer();

  const visibleMenuItems = [
    ...getSideMenuItems({
      currentPathName,
      enableAdoptionReporting,
      enableCatalog,
      enableNewSettingsPage,
      enablePriceUpdate,
      enableValueOverviewDashboard,
      isAutomated,
      openRfxDrawer,
      router,
      t,
      unreadCount: (isAdminChannelOptInEnabled && !isBadgeFilterError) ? filteredUnreadCount : unreadCount,
      user,
    }),
    ...getHelpMenuItemData(t, user.is_buyer, user.is_supplier, user.isSuperAdmin(), user.impersonating),
  ].filter(item => item.visible);

  // When toggling the side menu we need to update the location of the intercom launcher
  useEffect(() => {
    const intercomLauncher = document.querySelector('.intercom-lightweight-app-launcher') as HTMLElement;

    if (intercomLauncher) {
      const pixels = collapsed ? collapsedIntercomLeft : expandedIntercomLeft;

      intercomLauncher.style.left = `${pixels.toString()}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  return (
    <div className={`side-menu ${style.wrapper}`} id='side-menu'>
      <Layout.Sider
        breakpoint='sm'
        collapsed={collapsed}
        collapsible
        id='sider'
        trigger={null}
        width={SIDER_MAX_WIDTH}
      >
        <ArkestroLogo collapsed={collapsed} />
        <CloseOutlined className={style.closeBtn} onClick={toggleSideMenu} />
        <ul className={style.sideMenu}>
          {visibleMenuItems.map(item => {
            const activeClass = isActiveLink(router, style.active, item.path, item.id);

            return (
              <li
                key={`${item.text}:${item.path}`}
                className={combineClassNames(
                  style.sideMenuItem,
                  collapsed ? style.collapsedMenuItem : '',
                  activeClass
                )}
                data-testid={`${item.id}-menu-item`}
                title={item.children?.length ? undefined : (item.title || item.text as string)}
              >
                <LinkItem
                  childrenArray={item.children?.filter(child => child.visible)}
                  className={activeClass}
                  collapsed={collapsed}
                  iconClasses={[style.sideMenuItemIcon]}
                  item={item}
                  labelClasses={[style.sideMenuItemLabel]}
                />
              </li>
            );
          })}
        </ul>
        <Footer collapsed={collapsed} />
      </Layout.Sider>
      <Backdrop collapsed={collapsed} />
    </div>
  );
};
